import React, { useState, useEffect } from 'react';
import { ExternalLink, Phone, ChevronDown, ChevronRight } from 'lucide-react';
import { useTranslate } from '../lib/i18n';
import type { AISurveyAnswerResponse, HelpSource } from '../services/backendService';
import { safeGetHostname } from '../utils/urlUtils';
import Button from './common/Button';
import MarkdownRenderer from './MarkdownRenderer';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from './ui/collapsible';
import { Badge } from './ui/badge';
import { TagPill } from './TagPill';

interface HelpSourceItemProps {
  item: HelpSource;
  itemIndex: number;
  filter: 'online' | 'offline';
}

const HelpSourceItem = ({ item, itemIndex, filter }: HelpSourceItemProps) => {
  const t = useTranslate();

  const renderContact = (
    contact: { type: 'url' | 'phone'; value: string },
    contactIndex: number
  ) => {
    if (contact.type === 'url') {
      return (
        <div
          key={contactIndex}
          className="flex items-center bg-accent text-accent-foreground p-2 rounded-b-md"
        >
          <a
            href={contact.value}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-2 hover:underline"
          >
            <ExternalLink size={16} className="shrink-0" />
            <span>{safeGetHostname(contact.value)}</span>
          </a>
        </div>
      );
    }

    if (contact.type === 'phone') {
      return (
        <div
          key={contactIndex}
          className="flex items-center bg-accent text-accent-foreground p-2 rounded-b-md"
        >
          <a href={`tel:${contact.value}`} className="flex items-center gap-2 hover:underline">
            <Phone size={16} className="shrink-0" />
            <span>{contact.value}</span>
          </a>
        </div>
      );
    }

    return null;
  };

  const isRecommended = filter === 'offline';
  const isSuomiFi = item.contacts.some((c) => c.value.includes('suomi.fi'));

  return (
    <div key={itemIndex} className="rounded-md border bg-popover text-popover-foreground">
      <div className="p-2">
        <div className="flex items-center gap-2 mb-1">
          <span>{item.name}</span>
          {isRecommended && (
            <Badge
              variant="outline"
              className="text-sm px-2 py-0.5 ml-auto bg-muted text-muted-foreground"
            >
              {t('survey.recommendedSourcesOfHelp')}
            </Badge>
          )}
          {isSuomiFi && (
            <Badge
              variant="outline"
              className="text-sm px-2 py-0.5 ml-auto bg-muted text-muted-foreground"
            >
              {t('survey.suomiFiSource')}
            </Badge>
          )}
        </div>
        <p className="text-muted-foreground text-sm mt-1">{item.description}</p>
      </div>
      <div className="space-y-1">
        {item.contacts.map((contact, contactIndex) => renderContact(contact, contactIndex))}
      </div>
    </div>
  );
};

interface SurveyToolResponseProps {
  feedback: AISurveyAnswerResponse | null;
  onExport: () => void;
  userProfileTags: Record<string, boolean>;
  sessionPassword?: { code: string; expiresAt: string | null } | null;
  warnOnUnload?: boolean;
}

const SurveyToolResponse = ({
  feedback,
  onExport,
  userProfileTags,
  warnOnUnload = true,
}: SurveyToolResponseProps) => {
  const t = useTranslate();
  const [isVictimProfileOpen, setIsVictimProfileOpen] = useState(false);
  const [isCrimeOverviewOpen, setIsCrimeOverviewOpen] = useState(false);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [feedback]);

  useEffect(() => {
    if (!feedback || !warnOnUnload) {
      return undefined;
    }

    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = '';
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [feedback, warnOnUnload]);

  const renderAllHelpSources = (helpSources: HelpSource[]) => {
    // Sort to show recommended sources (offline) first, then online
    const sortedSources = [...helpSources].sort((a, b) => {
      if (a.source === 'offline' && b.source === 'online') return -1;
      if (a.source === 'online' && b.source === 'offline') return 1;
      return 0;
    });

    return sortedSources.map((item, itemIndex) => (
      <HelpSourceItem key={itemIndex} item={item} itemIndex={itemIndex} filter={item.source} />
    ));
  };

  if (!feedback) {
    return null;
  }

  return (
    <div className="min-h-full flex flex-col">
      <div className="max-w-lg mx-auto pt-4 pb-4 space-y-4">
        {/* Victim Profile Section */}
        <Collapsible open={isVictimProfileOpen} onOpenChange={setIsVictimProfileOpen}>
          <div className="border bg-card rounded-md">
            <CollapsibleTrigger className="w-full p-3 hover:bg-muted/50 transition-colors">
              <div className="flex items-center justify-between">
                <h5>{t('survey.victimProfile').toUpperCase()}</h5>
                <div className="flex items-center gap-2">
                  {userProfileTags['underage'] && <TagPill text={t('survey.victimIsMinor')} />}
                  {isVictimProfileOpen ? (
                    <ChevronDown className="size-6 text-muted-foreground" />
                  ) : (
                    <ChevronRight className="size-6 text-muted-foreground" />
                  )}
                </div>
              </div>
            </CollapsibleTrigger>
            <CollapsibleContent className="px-3 pb-3 text-muted-foreground">
              <MarkdownRenderer markdown={feedback.victim_profile || ''} />
            </CollapsibleContent>
          </div>
        </Collapsible>

        {/* Crime Overview Section */}
        <Collapsible open={isCrimeOverviewOpen} onOpenChange={setIsCrimeOverviewOpen}>
          <div className="border bg-card rounded-md">
            <CollapsibleTrigger className="w-full p-3 hover:bg-muted/50 transition-colors">
              <div className="flex items-center justify-between">
                <h5>{t('survey.crimeOverview').toUpperCase()}</h5>
                {isCrimeOverviewOpen ? (
                  <ChevronDown className="size-6 text-muted-foreground" />
                ) : (
                  <ChevronRight className="size-6 text-muted-foreground" />
                )}
              </div>
            </CollapsibleTrigger>

            {/* Preview when collapsed */}
            {!isCrimeOverviewOpen && (
              <div className="px-3 pb-3 text-muted-foreground relative">
                <div className="line-clamp-4 overflow-hidden">
                  <MarkdownRenderer markdown={feedback.crime_overview || ''} />
                </div>
                <div className="absolute bottom-0 left-0 right-0 h-12 bg-linear-to-t from-card to-transparent pointer-events-none rounded-b-md" />
              </div>
            )}

            <CollapsibleContent className="px-3 pb-3 text-muted-foreground">
              <MarkdownRenderer markdown={feedback.crime_overview || ''} />
              <span className="block mt-2">
                <MarkdownRenderer markdown={t('survey.overviewAdditionalInfo')} />
              </span>
            </CollapsibleContent>
          </div>
        </Collapsible>

        {/* Immediate Actions Section */}
        <div className="border bg-card rounded-md">
          <div className="p-4 pb-1">
            <h5 className="mb-4">{t('survey.doThisNow').toUpperCase()}</h5>
          </div>
          <div className="px-4 pb-4 space-y-3">
            {feedback.immediate_actions &&
              feedback.immediate_actions.map((action, index) => (
                <div key={index} className="border rounded-md p-4 bg-popover">
                  <div className="flex items-start gap-3">
                    <div className="font-medium text-card-foreground leading-relaxed">
                      {action.replace(/^\d+\.\s*/, '')}
                    </div>
                  </div>
                </div>
              ))}
          </div>
          <div className="border-t p-4 text-muted-foreground text-sm">
            {t('survey.actionInstructionsDisclaimer')}
          </div>
        </div>

        {/* Help Sources Section */}
        {feedback.help_sources && feedback.help_sources.length > 0 && (
          <div className="border bg-card rounded-md">
            <div className="p-4 pb-2">
              <h4 className="mb-3">{t('survey.helpSources').toUpperCase()}</h4>
            </div>
            <div className="px-3 pb-3 space-y-3">{renderAllHelpSources(feedback.help_sources)}</div>
          </div>
        )}

        <div className="border bg-card rounded-md">
          <div className="p-4">
            <a
              href="https://link.webropol.com/s/totpalaute"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 text-primary hover:underline"
            >
              <span>{t('survey.feedbackLink')}</span>
              <ExternalLink size={16} className="shrink-0" />
            </a>
          </div>
        </div>
      </div>
      <div className="bg-background border-t sticky bottom-0 mt-auto">
        <div className="py-4 max-w-lg mx-auto px-4 box-content">
          <div className="flex gap-2">
            <Button
              variant="primary"
              onClick={onExport}
              className="flex-1 flex items-center justify-center gap-2"
            >
              {t('survey.exportButton')}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SurveyToolResponse;
